import type { Block, BlockType } from '@ellie/shared';
import { BLOCK_META } from '@ellie/shared';

interface TableOfContentsProps {
  blocks: Block[];
  totalPages: number;
  /** Page number where the first exercise block starts (default: 3). */
  firstBlockPage?: number;
}

/**
 * Computes approximate page numbers per block.
 * Uses each block's `estimatedPages` field.
 */
function computePageNumbers(blocks: Block[], startPage: number): number[] {
  let currentPage = startPage;
  return blocks.map((block) => {
    const page = currentPage;
    currentPage += Math.ceil(block.estimatedPages);
    return page;
  });
}

export function TableOfContents({ blocks, totalPages, firstBlockPage = 3 }: TableOfContentsProps) {
  const pageNumbers = computePageNumbers(blocks, firstBlockPage);
  const tocPageNumber = firstBlockPage - 1;

  return (
    <div className="wb-page wb-toc">
      <h2>Table of Contents</h2>
      <p className="wb-toc-subtitle">
        {totalPages} pages, {blocks.length} exercises
      </p>

      <ul className="wb-toc-list">
        <div className="wb-toc-section-header">Exercises</div>

        {blocks.map((block, i) => {
          const meta = BLOCK_META[block.type as BlockType];
          return (
            <li key={block.id} className="wb-toc-item">
              <span className="wb-toc-number">{String(i + 1).padStart(2, '0')}</span>
              <span className="wb-toc-icon">{meta.emoji}</span>
              <span className="wb-toc-title">
                {block.title}
                <span>{block.titleNative}</span>
              </span>
              <span className="wb-toc-dots" />
              <span className="wb-toc-page">{pageNumbers[i]}</span>
            </li>
          );
        })}

        <div className="wb-toc-section-header">Reference</div>

        <li className="wb-toc-item">
          <span className="wb-toc-number" />
          <span className="wb-toc-icon">{'\u{1F4D5}'}</span>
          <span className="wb-toc-title">Verb Glossary</span>
          <span className="wb-toc-dots" />
          <span className="wb-toc-page">&mdash;</span>
        </li>
        <li className="wb-toc-item">
          <span className="wb-toc-number" />
          <span className="wb-toc-icon">{'\u{1F30E}'}</span>
          <span className="wb-toc-title">Cultural Corner</span>
          <span className="wb-toc-dots" />
          <span className="wb-toc-page">&mdash;</span>
        </li>
      </ul>

      <span className="wb-page-number">{tocPageNumber}</span>
    </div>
  );
}
